// SchemePay Reconciliation Dashboard - Core Reconciliation Engine
import {
  RazorpayColumnMapping,
  ErpColumnMapping,
  ReconciledItem,
  ReconciliationSummary,
  DailyCollectionTrend,
  ErpBillDetail,
  ReconciliationStatus,
} from '../types/reconciliation';

/**
 * Sanitizes numeric string or number into a 2-decimal rounded float
 */
export function sanitizeAmount(val: any): number {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') {
    return isNaN(val) ? 0 : Math.round((val + Number.EPSILON) * 100) / 100;
  }
  const str = String(val)
    .replace(/[₹$,\s]/g, '')
    .trim();
  const num = parseFloat(str);
  return isNaN(num) ? 0 : Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Normalizes Payment ID string (trims and handles casing)
 */
export function normalizePaymentId(val: any): string {
  if (val === null || val === undefined) return '';
  return String(val).trim();
}

export interface ReconciliationResult {
  items: ReconciledItem[];
  summary: ReconciliationSummary;
  trends: DailyCollectionTrend[];
}

/**
 * Reconciles Razorpay rows against ERP rows
 *
 * CRITICAL BUSINESS RULE:
 * A single Razorpay Payment ID can be linked to multiple ERP bills.
 * The system aggregates all ERP bills under the same Payment ID before comparison.
 */
export function runReconciliationEngine(
  razorpayRows: Record<string, any>[],
  erpRows: Record<string, any>[],
  rpMapping: RazorpayColumnMapping,
  erpMapping: ErpColumnMapping
): ReconciliationResult {
  const startTime = performance.now();

  // 1. Group ERP bills by Payment ID
  // Key: normalized Payment ID -> ERP aggregated details
  const erpGroups = new Map<
    string,
    {
      totalAmount: number;
      bills: ErpBillDetail[];
      billNumbers: string[];
      customerNames: Set<string>;
      postingDates: string[];
    }
  >();

  for (const row of erpRows) {
    const rawPid = row[erpMapping.paymentId];
    const pid = normalizePaymentId(rawPid);
    if (!pid) continue;

    const billNumber = String(row[erpMapping.billNumber] || '').trim();
    const billAmount = sanitizeAmount(row[erpMapping.billAmount]);
    const postingDate = String(row[erpMapping.postingDate] || '').trim();
    const custName = String(row[erpMapping.customerName] || '').trim();

    if (!erpGroups.has(pid)) {
      erpGroups.set(pid, {
        totalAmount: 0,
        bills: [],
        billNumbers: [],
        customerNames: new Set(),
        postingDates: [],
      });
    }

    const grp = erpGroups.get(pid)!;
    grp.totalAmount = Math.round((grp.totalAmount + billAmount + Number.EPSILON) * 100) / 100;
    if (billNumber) grp.billNumbers.push(billNumber);
    if (custName) grp.customerNames.add(custName);
    if (postingDate) grp.postingDates.push(postingDate);

    grp.bills.push({
      billNumber: billNumber || `BILL-${grp.bills.length + 1}`,
      billAmount,
      postingDate,
      customerName: custName,
      raw: row,
    });
  }

  const reconciledItems: ReconciledItem[] = [];
  const processedErpPids = new Set<string>();

  let exactMatches = 0;
  let partialMatches = 0;
  let overBilled = 0;
  let noBillReference = 0;
  let erpOnly = 0;
  let refundedCount = 0;

  let totalCollectionAmount = 0;
  let totalReconciledAmount = 0;
  let totalExceptionAmount = 0;

  const dateTrendMap = new Map<
    string,
    { razorpay: number; erp: number; exact: number; exceptions: number }
  >();

  // 2. Iterate through Razorpay records and reconcile against ERP groups
  for (const row of razorpayRows) {
    const rawPid = row[rpMapping.paymentId];
    const pid = normalizePaymentId(rawPid);
    if (!pid) continue;

    const rpAmount = sanitizeAmount(row[rpMapping.amount]);
    const custName = String(row[rpMapping.customerName] || '').trim();
    const paymentDate = String(row[rpMapping.paymentDate] || '').trim();
    const rpStatus = String(row[rpMapping.status] || '').toLowerCase().trim();
    const refundAmount = sanitizeAmount(row[rpMapping.refundAmount]);
    const refundDate = String(row[rpMapping.refundDate] || '').trim();

    totalCollectionAmount = Math.round((totalCollectionAmount + rpAmount + Number.EPSILON) * 100) / 100;

    const dateKey = paymentDate ? paymentDate.split(' ')[0].split('T')[0] : 'Unspecified';
    if (!dateTrendMap.has(dateKey)) {
      dateTrendMap.set(dateKey, { razorpay: 0, erp: 0, exact: 0, exceptions: 0 });
    }
    const trend = dateTrendMap.get(dateKey)!;
    trend.razorpay = Math.round((trend.razorpay + rpAmount + Number.EPSILON) * 100) / 100;

    const hasErpGroup = erpGroups.has(pid);
    const erpGroup = hasErpGroup ? erpGroups.get(pid)! : null;

    if (hasErpGroup) {
      processedErpPids.add(pid);
      trend.erp = Math.round((trend.erp + erpGroup!.totalAmount + Number.EPSILON) * 100) / 100;
    }

    const erpTotalAmount = erpGroup ? erpGroup.totalAmount : 0;
    const diff = Math.round((rpAmount - erpTotalAmount + Number.EPSILON) * 100) / 100;
    const billCount = erpGroup ? erpGroup.bills.length : 0;
    const billNumbers = erpGroup ? erpGroup.billNumbers : [];
    const bills = erpGroup ? erpGroup.bills : [];

    const effectiveCustomerName =
      custName || (erpGroup && erpGroup.customerNames.size > 0 ? Array.from(erpGroup.customerNames)[0] : 'N/A');

    let status: ReconciliationStatus;
    let aiNotes = '';
    let aiSuggestedAction = '';

    // RULE 6: Refund check
    const isRefunded =
      rpStatus.includes('refund') ||
      refundAmount > 0 ||
      (refundDate !== '' && refundDate !== 'null' && refundDate !== 'undefined');

    if (isRefunded) {
      status = 'Refunded';
      refundedCount++;
      totalExceptionAmount = Math.round((totalExceptionAmount + (refundAmount || rpAmount) + Number.EPSILON) * 100) / 100;
      aiNotes = `Transaction marked as refunded in Razorpay. Refund amount: ₹${(refundAmount || rpAmount).toLocaleString('en-IN')}.`;
      aiSuggestedAction = 'Verify credit note / reverse entry posted in ERP for refund reversal.';
      trend.exceptions++;
    } else if (!hasErpGroup || billCount === 0) {
      // RULE 4: No Bill Reference
      status = 'No Bill Reference';
      noBillReference++;
      totalExceptionAmount = Math.round((totalExceptionAmount + rpAmount + Number.EPSILON) * 100) / 100;
      aiNotes = `Payment received in Razorpay (₹${rpAmount.toLocaleString('en-IN')}) but no corresponding ERP bills exist with Payment ID ${pid}.`;
      aiSuggestedAction = 'Check Scheme App pending order queue or generate manual bill in ERP against Payment ID.';
      trend.exceptions++;
    } else if (Math.abs(diff) < 0.01) {
      // RULE 1: Exact Match
      status = 'Exact Match';
      exactMatches++;
      totalReconciledAmount = Math.round((totalReconciledAmount + rpAmount + Number.EPSILON) * 100) / 100;
      aiNotes = `Exact match across ${billCount} ERP bill(s). Sum equals ₹${rpAmount.toLocaleString('en-IN')}.`;
      aiSuggestedAction = 'Automatic clearance eligible. No manual action required.';
      trend.exact++;
    } else if (diff > 0) {
      // RULE 2: Partial Match (Razorpay Amount > ERP Total Amount)
      status = 'Partial Match';
      partialMatches++;
      totalExceptionAmount = Math.round((totalExceptionAmount + diff + Number.EPSILON) * 100) / 100;
      aiNotes = `Customer paid ₹${rpAmount.toLocaleString('en-IN')} in Razorpay, but ERP only billed ₹${erpTotalAmount.toLocaleString('en-IN')} across ${billCount} bill(s). Unbilled balance: ₹${diff.toLocaleString('en-IN')}.`;
      aiSuggestedAction = 'Verify if an additional scheme installment or tax invoice is pending bill generation.';
      trend.exceptions++;
    } else {
      // RULE 3: Over Billed (ERP Total Amount > Razorpay Amount)
      status = 'Over Billed';
      overBilled++;
      const absDiff = Math.abs(diff);
      totalExceptionAmount = Math.round((totalExceptionAmount + absDiff + Number.EPSILON) * 100) / 100;
      aiNotes = `ERP total (₹${erpTotalAmount.toLocaleString('en-IN')} across ${billCount} bill(s)) exceeds payment received (₹${rpAmount.toLocaleString('en-IN')}) by ₹${absDiff.toLocaleString('en-IN')}.`;
      aiSuggestedAction = 'Check if duplicate bill was posted in ERP or if customer made a split payment under another reference.';
      trend.exceptions++;
    }

    reconciledItems.push({
      id: `recon_${pid}_${Math.random().toString(36).substring(2, 6)}`,
      paymentId: pid,
      customerName: effectiveCustomerName,
      razorpayAmount: rpAmount,
      erpTotalAmount,
      differenceAmount: diff,
      billCount,
      billNumbers,
      erpBills: bills,
      paymentDate: paymentDate || 'N/A',
      refundStatus: isRefunded ? 'Refunded' : 'None',
      refundAmount: refundAmount > 0 ? refundAmount : isRefunded ? rpAmount : 0,
      refundDate: refundDate || (isRefunded ? paymentDate : 'N/A'),
      status,
      aiNotes,
      aiSuggestedAction,
    });
  }

  // 3. Process remaining ERP groups that have NO corresponding Razorpay payment (RULE 5: ERP Only)
  for (const [pid, erpGroup] of erpGroups.entries()) {
    if (processedErpPids.has(pid)) continue;

    erpOnly++;
    totalExceptionAmount = Math.round((totalExceptionAmount + erpGroup.totalAmount + Number.EPSILON) * 100) / 100;

    const primaryPostingDate = erpGroup.postingDates[0] || 'N/A';
    const dateKey = primaryPostingDate ? primaryPostingDate.split(' ')[0].split('T')[0] : 'Unspecified';

    if (!dateTrendMap.has(dateKey)) {
      dateTrendMap.set(dateKey, { razorpay: 0, erp: 0, exact: 0, exceptions: 0 });
    }
    const trend = dateTrendMap.get(dateKey)!;
    trend.erp = Math.round((trend.erp + erpGroup.totalAmount + Number.EPSILON) * 100) / 100;
    trend.exceptions++;

    const primaryCustomer =
      erpGroup.customerNames.size > 0 ? Array.from(erpGroup.customerNames)[0] : 'ERP Client';

    reconciledItems.push({
      id: `recon_erponly_${pid}_${Math.random().toString(36).substring(2, 6)}`,
      paymentId: pid,
      customerName: primaryCustomer,
      razorpayAmount: 0,
      erpTotalAmount: erpGroup.totalAmount,
      differenceAmount: -erpGroup.totalAmount,
      billCount: erpGroup.bills.length,
      billNumbers: erpGroup.billNumbers,
      erpBills: erpGroup.bills,
      paymentDate: 'N/A',
      refundStatus: 'None',
      refundAmount: 0,
      refundDate: 'N/A',
      status: 'ERP Only',
      aiNotes: `Bills exist in ERP totaling ₹${erpGroup.totalAmount.toLocaleString('en-IN')} with Payment ID ${pid}, but no Razorpay payment transaction was found.`,
      aiSuggestedAction: 'Check if payment was processed through offline NEFT/Cheque or if payment gateway webhook failed.',
    });
  }

  const totalGroups = reconciledItems.length;
  const matchPercentage =
    totalGroups > 0 ? Math.round(((exactMatches / totalGroups) * 100 + Number.EPSILON) * 100) / 100 : 0;

  const endTime = performance.now();
  const processingTimeMs = Math.round(endTime - startTime);

  // Convert date trends to sorted array
  const trends: DailyCollectionTrend[] = Array.from(dateTrendMap.entries())
    .map(([date, data]) => ({
      date,
      razorpayAmount: data.razorpay,
      erpAmount: data.erp,
      exactMatches: data.exact,
      exceptions: data.exceptions,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const summary: ReconciliationSummary = {
    totalRazorpayRecords: razorpayRows.length,
    totalErpRecords: erpRows.length,
    totalGroups,
    exactMatches,
    partialMatches,
    overBilled,
    noBillReference,
    erpOnly,
    refundedCount,
    totalCollectionAmount,
    totalReconciledAmount,
    totalExceptionAmount,
    matchPercentage,
    processingTimeMs,
  };

  return {
    items: reconciledItems,
    summary,
    trends,
  };
}
